/**
 * Resources Vuex module for Portal Application
 * Controls the data and actions for resources such as files and links
 * 
 * @module Resources
 * @see
 * @author Bailey Kulman (Fresche Solutions)
 * @date December 2024
 */

import portalapi from "@services/portalapi";
import { resetStateData } from "@/utils/vuexStoreUtils";
import { getField, updateField } from "vuex-map-fields";

const DEFAULT_STATE = {
    allResources: [],
    selectedResource: null,
    displayResources: [],
    resourceTypes: [],
    resourceTypesLoaded: false,
    allResourceTypes: [],
    selectedResourceType: null,
    resourceGroupNames: [],
    resourceTypesForAdmin: [],
};

const state = structuredClone(DEFAULT_STATE);

const getters = {
    getField, // for vuex-map-fields

    getAllResources(st) {
        return st.allResources;
    },

    getSelectedResource(st) {
        return st.selectedResource;
    },

    getDisplayResources(st) {
        return st.displayResources;
    },

    getAllResourceTypes(st) {
        return st.allResourceTypes;
    },

    getSelectedResourceType(st) {
        return st.selectedResourceType;
    },

    /**
     * Returns true once the resource types have been fetched and stored.
     */
    getResourceTypesLoaded(st) {
        return st.resourceTypesLoaded;
    },

    /**
     * Returns the full array of active resource type objects from the API.
     */
    getResourceTypes(st) {
        return st.resourceTypes;
    },

    /**
     * Returns the display configuration object for a given resource type id.
     * Mirrors the shape previously provided by RESOURCE_TYPE_DISPLAY_OPTIONS in ResourceConfiguration.js.
     * Uses loose equality to handle both number and string id values.
     * @param {number|string} id - The resource type primary key
     */
    getResourceTypeDisplayOptions: (st) => (id) => {
        // eslint-disable-next-line eqeqeq
        const rt = st.resourceTypes.find((rt) => rt.id == id);
        
        if (!rt) {
            return null;
        }

        return {
            standAloneResource: rt.standAloneResource,
            showDates: rt.showDates,
            usePagination: rt.usePagination,
            useSearch: rt.useSearch,
            displayText: rt.displayText,
            toolTipText: rt.toolTipText,
            sortData: rt.sortData,
        };
    },

    /**
     * Returns an array of all resource type objects (active and inactive) with id, description, and isActive.
     * Used by the admin resource form so resources can be assigned to inactive resource types.
     */
    getResourceTypeOptionsAll(st) {
        return st.resourceTypesForAdmin.map((rt) => ({ id: rt.id, description: rt.description, isActive: rt.isActive }));
    },

    /**
     * Returns the human-readable description for a given resource type id.
     * Searches active resource types first, then falls back to the admin list so that
     * inactive resource types are still resolved to their description.
     * Uses loose equality to handle both number and string id values.
     * @param {number|string} id - The resource type primary key
     * @returns {string} The description, or the id as a string if not found
     */
    getResourceTypeDescription: (st) => (id) => {
        // eslint-disable-next-line eqeqeq
        const rt = st.resourceTypes.find((rt) => rt.id == id)
            // eslint-disable-next-line eqeqeq
            ?? st.resourceTypesForAdmin.find((rt) => rt.id == id)
            ?? null;
        return rt ? rt.description : String(id);
    },

    /**
     * Returns the full resource type object for a given id, including its authorities.
     * Searches active resource types first, then falls back to the admin list so that
     * inactive resource types (e.g. on an existing resource record) are still resolved.
     * Uses loose equality to handle both number and string id values.
     * @param {number|string} id - The resource type primary key
     * @returns {object|null} The resource type object, or null if not found
     */
    getResourceTypeById: (st) => (id) => {
        // eslint-disable-next-line eqeqeq
        return st.resourceTypes.find((rt) => rt.id == id)
            // eslint-disable-next-line eqeqeq
            ?? st.resourceTypesForAdmin.find((rt) => rt.id == id)
            ?? null;
    },

    /**
     * Returns the full resource type object for a given blobFolder slug.
     * @param {string} blobFolder - The blobFolder slug
     * @returns {object|null} The resource type object, or null if not found
     */
    getResourceTypeByBlobFolder: (st) => (blobFolder) => {
        return st.resourceTypes.find((rt) => rt.blobFolder === blobFolder) ?? null;
    },

    /**
     * Builds the nav link structure for the Resources side menu.
     *
     * @param {string|null} standaloneGroupName - When provided, returns only the child link
     *   entries for that standalone resource group (i.e. resource types that share this
     *   resourceGroup AND have standAloneResource === true).  When omitted / null, returns
     *   the standard Resources nav which excludes all standalone resource types.
     *
     * This keeps the two nav contexts completely separate:
     *   - Regular Resources: no standalone items appear.
     *   - Standalone resource group: only that group's children appear.
     */
    getNavLinks: (st) => (standaloneGroupName = null) => {
        const NAVBAR_ITEM_TYPE_LINK = vm.config.globalProperties.$constants.NAVBAR_ITEM_TYPE_LINK;
        const NAVBAR_ITEM_TYPE_DROPDOWN = vm.config.globalProperties.$constants.NAVBAR_ITEM_TYPE_DROPDOWN;

        const sortedResourceTypes = [...st.resourceTypes].sort((a, b) => {
            const aOrder = a.displayOrder ?? Infinity;
            const bOrder = b.displayOrder ?? Infinity;
            return aOrder - bOrder;
        });

        // ── Standalone group context ──────────────────────────────────────────
        // Return only the children of the requested standalone group as flat
        // NAVBAR_ITEM_TYPE_LINK entries (no nested dropdowns needed).
        if (standaloneGroupName) {
            return sortedResourceTypes
                .filter((rt) => rt.standAloneResource && rt.resourceGroup === standaloneGroupName)
                .map((rt) => ({
                    type: NAVBAR_ITEM_TYPE_LINK,
                    name: rt.description,
                    id: rt.id,
                    blobFolder: rt.blobFolder,
                    // Standalone resources use top-level routes (/:blobFolder),
                    // not the /helpAndResources/:resourceType route.
                    link: `/#/${rt.blobFolder}`,
                }));
        }

        // ── Regular Resources context ─────────────────────────────────
        // Track the minimum displayOrder seen for each group so the group header
        // can be inserted at the correct position in the final sorted list.
        const groupMinOrder = {};
        // childLinks arrays for each group, already in displayOrder sequence.
        const groupMap = {};
        // Top-level (no resourceGroup) nav entries with their displayOrder.
        const standaloneEntries = [];

        sortedResourceTypes.forEach((rt) => {
            // Exclude all standalone resource types from the regular nav —
            // they appear in their own standalone group nav context instead.
            if (rt.standAloneResource) return;

            // All non-standalone resource types resolve to /helpAndResources/<blobFolder>
            const href = `/#/helpAndResources/${rt.blobFolder}`;
            const order = rt.displayOrder ?? Infinity;

            if (!rt.resourceGroup) {
                standaloneEntries.push({
                    displayOrder: order,
                    entry: {
                        type: NAVBAR_ITEM_TYPE_LINK,
                        name: rt.description,
                        id: rt.id,
                        blobFolder: rt.blobFolder,
                        link: href,
                    },
                });
            } else {
                if (!groupMap[rt.resourceGroup]) {
                    groupMap[rt.resourceGroup] = [];
                    groupMinOrder[rt.resourceGroup] = order;
                } else if (order < groupMinOrder[rt.resourceGroup]) {
                    groupMinOrder[rt.resourceGroup] = order;
                }
                groupMap[rt.resourceGroup].push({
                    name: rt.description,
                    id: rt.id,
                    blobFolder: rt.blobFolder,
                    link: href,
                    parent: rt.resourceGroup,
                });
            }
        });

        // Build dropdown entries for each resourceGroup, keyed by their minimum displayOrder.
        // Group landing pages also use the parameterized route:
        // /helpAndResources/<groupName> — the component will default to the
        // first authorized child when no specific resourceType param is present.
        const groupEntries = Object.keys(groupMap).map((groupName) => {
            const groupHref = `/#/helpAndResources/${groupName}`;
            return {
                displayOrder: groupMinOrder[groupName],
                entry: {
                    type: NAVBAR_ITEM_TYPE_DROPDOWN,
                    groupName: groupName,
                    link: groupHref,
                    displayName: groupName,
                    childLinks: groupMap[groupName],
                },
            };
        });

        // Merge standalone entries and group entries, then sort by displayOrder
        // to produce the final correctly-ordered nav list.
        return [...standaloneEntries, ...groupEntries]
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((item) => item.entry);
    },
};

const mutations = {
    updateField, // for vuex-map-fields

    async clearStateData(st) {
        st = resetStateData(st, DEFAULT_STATE);
    },

    setAllResources(st, payload) {
        st.allResources = payload;
    },

    setSelectedResource(st, payload) {
        st.selectedResource = payload;
    },

    setDisplayResources(st, payload) {
        st.displayResources = payload;
    },

    setResourceTypes(st, payload) {
        st.resourceTypes = payload;
        st.resourceTypesLoaded = true;
    },

    setAllResourceTypes(st, payload) {
        st.allResourceTypes = payload;
    },

    setSelectedResourceType(st, payload) {
        st.selectedResourceType = payload;
    },

    setResourceGroupNames(st, payload) {
        st.resourceGroupNames = payload;
    },

    setResourceTypesForAdmin(st, payload) {
        st.resourceTypesForAdmin = payload;
    },

    /**
     * Resets the resourceTypesLoaded flag so the next call to fetchResourceTypes
     * will re-fetch from the API (e.g. after display order is updated).
     */
    resetResourceTypesLoaded(st) {
        st.resourceTypesLoaded = false;
    },
};

const actions = {
    /**
     * Fetches all active resource types with display configuration from the API.
     * Skips the request if data has already been loaded (cached in state).
     */
    async fetchResourceTypes({ commit, state }) {
        if (state.resourceTypesLoaded) return;

        const response = await portalapi.moduleResources.getResourceTypes();

        if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            commit('setResourceTypes', response.data);
        }
    },

    
    // Fetch All Resources
    async fetchAllResources({ commit }, payload)
    {
        const requestPayload = {
            page: (payload && payload.page ? payload.page : 1),
            itemsPerPage: (payload && payload.itemsPerPage ? payload.itemsPerPage : 10),
            filters: (payload && payload.filters ? payload.filters : []),
            sortBy: (payload && payload.sortBy ? payload.sortBy : {column: "id", state: 0}),
        };

        const response = await portalapi.moduleResources.getResourcesPage(requestPayload);

        if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            commit("setAllResources", response.data);
        }
    },


    // Make API call to update a resource's expiry date
    async toggleResourceExpiry({ commit }, id) 
    {
        return await portalapi.moduleResources.toggleResourceExpiry(id);
    },


    // Make API call to toggle a resource's priority
    async toggleResourcePriority({ commit }, id) 
    {
        return await portalapi.moduleResources.togglePriority(id);
    },
    

    // Get selected resource
    async fetchSelectedResource({ commit }, resourceId) 
    {
        let response = await portalapi.moduleResources.getSelectedResourceData(resourceId);
        let responseData = {};
        
        // Check if the user was found
        if (!response) {
            responseData = {
                resourceFound: false,
            };
        } else {
            responseData = response.data;
            responseData.resourceFound = true;

            if (responseData.expiryDate !== null) {
                responseData.expiryDate = vm.config.globalProperties.$filters.date(responseData.expiryDate);
            }
            if (responseData.publishDate !== null) {
                responseData.publishDate = vm.config.globalProperties.$filters.date(responseData.publishDate);
            }
        }

        commit("setSelectedResource", responseData);
    },
    

    // Create resource
    async createResource({ commit }, resourceData) 
    {
        let requestData = {
            data: resourceData
        };

        let response = await portalapi.moduleResources.createResource(requestData);
    
        if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.CREATED) {
            await actions.fetchAllResources({ commit });
        }
    
        return response;
    },

    
    // Update selected resource
    async updateSelectedResource({ commit }, resourceData) 
    {
        let requestData = {
            data: resourceData
        };
    
        let response = await portalapi.moduleResources.updateResource(requestData);
    
        if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK && response.data.status == vm.config.globalProperties.$constants.STATUS_SUCCESS) {
            await commit("setSelectedResource", resourceData);
    
            await actions.fetchAllResources({ commit });
        }
    
        return response;
    },


    // Delete selected resource
    async deleteSelectedResource({ commit }, resourceId)
    {
        return await portalapi.moduleResources.deleteResource(resourceId);
    },


    // Fetch Resources by type
    // data.resourceType is the resource type id (integer)
    async fetchResourcesByType({ commit }, data)
    {
        const resourceTypeId = data.resourceType;
        const payload = data.payload;

        const response = await portalapi.moduleResources.getResourcesByType(resourceTypeId, payload);
        
        if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            commit("setDisplayResources", response.data);
        }
    },


    // Opens the resource in a new tab
    async displayResource({ commit }, resource) 
    {
        // Open resource link in new tab
        if (resource.contentType === vm.config.globalProperties.$constants.RESOURCE_CONTENT_TYPE_LINK) {
            window.open(resource.resourcePath, '_blank');
        }
        // Retrieve and open resource file in new tab
        else {
            // Retrieve file
            let response = await portalapi.moduleResources.getResourceFile(resource.id);

            if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
                try {

                    // Handle PDF files
                    if (resource.resourcePath.toLowerCase().endsWith('.pdf')) {
                        // Create blob from response
                        const blob = new Blob([response.data], { type: 'application/pdf' });
                        const blobUrl = window.URL.createObjectURL(blob);

                        // Open in new tab
                        window.open(blobUrl, '_blank');
                        
                        // Clean up
                        setTimeout(() => {
                            window.URL.revokeObjectURL(blobUrl);
                        }, 100);

                    }
                    // Handle Excel files
                    else if (resource.resourcePath.toLowerCase().endsWith('.xlsx')) {
                        // Create blob from response
                        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                        
                        // Create download link
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', resource.resourcePath);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        
                        // Clean up
                        setTimeout(() => window.URL.revokeObjectURL(url), 100);
                    }

                } catch (error) {
                    vm.config.globalProperties.$toast.open({
                        message: 'Error opening resource file.',
                        type: 'error',
                    });
                }

            } else if (response && response.response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.FORBIDDEN) {
                vm.config.globalProperties.$toast.open({
                    message: 'You do not have permission to access this resource.',
                    type: 'warning',
                });
            } else if (response && response.response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.NOT_FOUND) {
                vm.config.globalProperties.$toast.open({
                    message: 'Resource file not found.',
                    type: 'warning',
                });
            } else {
                vm.config.globalProperties.$toast.open({
                    message: 'Error retrieving resource file.',
                    type: 'error',
                });
            }
        }
    },

    // -------------------------------------------------------------------------
    // Resource Type Admin Actions
    // -------------------------------------------------------------------------

    /**
     * Fetches all resource types (paginated) for the admin management page.
     */
    async fetchAllResourceTypes({ commit }, payload) {
        const requestPayload = {
            page: (payload && payload.page ? payload.page : 1),
            itemsPerPage: (payload && payload.itemsPerPage ? payload.itemsPerPage : 10),
            filters: (payload && payload.filters ? payload.filters : []),
            sortBy: (payload && payload.sortBy ? payload.sortBy : { column: 'id', state: 0 }),
        };

        const response = await portalapi.moduleResources.getResourceTypesPage(requestPayload);

        if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            commit('setAllResourceTypes', response.data);
        }
    },

    /**
     * Fetches a single resource type by id for the edit form.
     */
    async fetchSelectedResourceType({ commit }, resourceTypeId) {
        let response = await portalapi.moduleResources.getSelectedResourceTypeData(resourceTypeId);
        let responseData = {};

        if (!response || !response.data) {
            responseData = { resourceTypeFound: false };
        } else {
            responseData = response.data;
            responseData.resourceTypeFound = true;
        }

        commit('setSelectedResourceType', responseData);
    },

    /**
     * Creates a new resource type.
     */
    async createResourceType({ commit }, resourceTypeData) {
        let requestData = { data: resourceTypeData };

        let response = await portalapi.moduleResources.createResourceType(requestData);

        if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.CREATED) {
            await actions.fetchAllResourceTypes({ commit });
        }

        return response;
    },

    /**
     * Updates the selected resource type.
     */
    async updateSelectedResourceType({ commit }, resourceTypeData) {
        let requestData = { data: resourceTypeData };

        let response = await portalapi.moduleResources.updateResourceType(requestData);

        if (
            response &&
            response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK &&
            response.data.status === vm.config.globalProperties.$constants.STATUS_SUCCESS
        ) {
            commit('setSelectedResourceType', resourceTypeData);
            await actions.fetchAllResourceTypes({ commit });
        }

        return response;
    },

    /**
     * Deletes the selected resource type.
     */
    async deleteSelectedResourceType({ commit }, resourceTypeId) {
        return await portalapi.moduleResources.deleteResourceType(resourceTypeId);
    },

    /**
     * Toggles a resource type's active status.
     */
    async toggleResourceTypeActive({ commit }, resourceTypeId) {
        return await portalapi.moduleResources.toggleResourceTypeActive(resourceTypeId);
    },

    /**
     * Syncs the authorities for a resource type (replaces all existing with the provided list).
     */
    async updateResourceTypeAuthorities({ commit }, { resourceTypeId, customerGroups }) {
        return await portalapi.moduleResources.updateResourceTypeAuthorities(resourceTypeId, customerGroups);
    },

    /**
     * Fetches all distinct resource group names (active + inactive types) for the datalist.
     */
    async fetchResourceGroupNames({ commit }) {
        const response = await portalapi.moduleResources.getResourceGroups();
        if (response && response.data && Array.isArray(response.data)) {
            commit('setResourceGroupNames', response.data);
        }
    },

    /**
     * Fetches all resource types (active and inactive) as a simple id/description/isActive list
     * for use in admin form dropdowns (e.g. the resource type selector on the resource form).
     * Results are cached in state; subsequent calls are no-ops unless the cache is cleared.
     */
    async fetchResourceTypesForAdmin({ commit, state }) {
        if (state.resourceTypesForAdmin.length > 0) return;

        const response = await portalapi.moduleResources.getResourceTypesForAdmin();

        if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            commit('setResourceTypesForAdmin', response.data);
        }
    },

    /**
     * Fetches all resource types with display order data for the Update Display Order modal.
     * Returns the raw response so the caller can handle the data directly.
     */
    async fetchResourceTypesForOrdering() {
        return await portalapi.moduleResources.getResourceTypesDisplayOrder();
    },

    /**
     * Bulk-updates the displayOrder for a set of resource types.
     * Resets the resourceTypesLoaded cache flag on success so the navbar
     * re-fetches the freshly ordered list on next render.
     * @param {Array<{ id: number, displayOrder: number }>} order
     */
    async updateResourceTypeDisplayOrder({ commit }, order) {
        const response = await portalapi.moduleResources.updateResourceTypeDisplayOrder(order);

        if (
            response &&
            response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK &&
            response.data.status === vm.config.globalProperties.$constants.STATUS_SUCCESS
        ) {
            commit('resetResourceTypesLoaded');
        }

        return response;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};